import React from 'react';
import { Flex } from '@common/components/Flex';
import { I18n } from 'react-redux-i18n';
import { getTestAttributes } from '@common/utils/tests';
import { FormControl } from '@common/components/form-control';
import TreeDropdown from '@common/components/TreeDropdown';
import { Radio } from '@common/components/radio';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { HttpErrorDto } from '@common/dto/http';
import { InvitePedigreeCreateDto } from '@common/dto/invite/pedigree/create-dto';
import { PedigreeGender } from '@common/dto/pedigree/gender';

import './patient-details.styles.scss';

export type PatientDetailsProps = {
  showErrorMessage: (msg: string) => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  data?: Partial<InvitePedigreeCreateDto>;
  onChange: (value: Partial<InvitePedigreeCreateDto>) => void;
};

export const PatientDetails: React.FC<PatientDetailsProps> = (props) => { 
  const { data, onChange, getFolders, showErrorMessage } = props;
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);

  React.useEffect(() => {
    getFolders().then(setFolders).catch((err) => {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get folders'));
    });

    onChange({ gender: PedigreeGender.female, ...data });
  }, []);

  const handleNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, pedigreeName: e.target.value.trim() });
  }, [onChange, data]);

  const handleFolderChange = React.useCallback((id: FolderPrimaryListDto['id'], folder: FolderPrimaryListDto) => {
    onChange({ ...data, targetFolderId: id });
  }, [onChange, data]);

  const handleGenderChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, gender: Number(e.target.value) });
  }, [onChange, data]);

  const handleEmailChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, email: e.target.value.trim() });
  }, [onChange, data]);

  const genderOptions = React.useMemo(() => {
    return [
      { label: I18n.t('Male'), value: PedigreeGender.male, id: 'radio-bw-first-step-combined-male-box-input' }, 
      { label: I18n.t('Female'), value: PedigreeGender.female, id: 'radio-bw-first-step-combined-female-box-input', },
    ];
  }, []);

  const fields = [
    {
      required: true,
      label: I18n.t('Folder Name'),
      component: (
        <TreeDropdown 
          className="InviteStatusPatientDetails__input"
          items={folders}
          value={data?.targetFolderId}
          onChange={handleFolderChange}
          {...getTestAttributes({ id: 'btn-bw-first-step-combined-drop' })}
        />
      )
    },
    {
      required: true,
      label: I18n.t('Pedigree Name'),
      component: (
        <FormControl 
          className="InviteStatusPatientDetails__input"
          defaultValue={data?.pedigreeName}
          onBlur={handleNameChange} 
        />
      )
    },
    {
      label: I18n.t('Sex'),
      component: (
        <div className="InviteStatusPatientDetails__input">
          {genderOptions.map((option) => (
            <Radio
              key={option.value}
              className="InviteStatusPatientDetails__sex-item"
              value={option.value}
              checked={data?.gender === option.value}
              onChange={handleGenderChange}
              {...getTestAttributes({ id: option.id })}
            >
              {option.label}
            </Radio>
          ))}
        </div>
      )
    },
    {
      required: true,
      label: I18n.t('Email'),
      component: (
        <FormControl 
          className="InviteStatusPatientDetails__input"
          defaultValue={data?.email}
          onBlur={handleEmailChange}
        />
      )
    },
  ];

  return (
    <div className="InviteStatusPatientDetails">
      {fields.map((field) => (
        <Flex key={field.label} justify="space-between" className="InviteStatusPatientDetails__section">
          <h6 className="InviteStatusPatientDetails__label">{field.label}{field.required && <span className="InviteStatusPatientDetails__label--required">*</span>}</h6>
          {field.component}
        </Flex>
      ))}
    </div>
  );
};